import { ToNumber } from "@pdfking/math"
import {
  MERGE_HEADER,
  MERGE_META_DIGITS,
  MERGE_SECTION_DIGITS,
  MERGE_SECTION_MARKER,
  MERGE_SHARED_MARKER,
} from "@pdfking/pdfium"
import type { PDFPageMeta } from "@pdfking/renderer-api"
import { Base64Decode, JSONDecode, UInt8ToString } from "@pdfking/tfunc"

// The merge output, written by pdfium's merge, read here (its constants are
// pdfium's MERGE_*). It is one page after the other, each a whole single-page
// pdf whose second line says how long it is and what it holds, so a reader
// cuts the download at section ends and hands every page over as it is:
//
//   %PDF-1.5
//   %PDF-Meta <section length, 16 digits> <meta length, 8 digits> <meta>
//
// the meta a base64 json of PDFPageMeta, so it stays on one line
//   the page's objects, catalog (1) with the page's layers, a page tree (2)
//   holding just that page, the section's own xref and trailer
//
// then, after the last page, a tail with no marker: the catalog (1) with
// every page's layers, the page tree (2) holding every page and the xref of
// the whole file. Sections index their objects relative to their own start;
// only the tail's xref (no /Prev) indexes the file, so to anyone else it is
// one pdf and the rest are bytes nothing points at.
//
// What the whole document shares — the output intent and its ICC profile, the
// XMP — is the first page's, and is written once, before the first page, as
// the shared section:
//
//   %PDF-1.5
//   %PDF-Base <section length, 16 digits> <meta length, 8 digits> <meta>
//
// the meta plain text: the first object's number, then where each object is
// from their start (`f` for one not written), then the objects alone. Every catalog, the sections' too, refers to them: in the
// file the tail's xref finds them, and a section cut out on its own gets
// them appended (see pdfium's shared_update) — a reference to an object a
// pdf does not have is null.
//
// and in front of it all, written once the merge is over, the integrity line
// — fixed length, nothing else in it:
//
//   %PDF-1.5
//   %PDF-Trim <file length, 16 digits>
//
// A merge streams, so its length is only known once it is over; and only a
// file still that long is the one the merge wrote — content appended later
// (an incremental save, a signature) lives in the tail the sections never
// read. 0 is a length nobody knew, and is not checked. The merge leaves room
// for the line, starting every offset it writes at INTEGRITY_LENGTH.

const START = `${MERGE_HEADER}${MERGE_SECTION_MARKER}`
const SHARED_START = `${MERGE_HEADER}${MERGE_SHARED_MARKER}`

// the fixed start of every section: header, marker, both lengths
export const SECTION_PREFIX = START.length + MERGE_SECTION_DIGITS + 2 + MERGE_META_DIGITS

const LENGTHS = new RegExp(`^(\\d{${MERGE_SECTION_DIGITS}}) (\\d{${MERGE_META_DIGITS}}) $`)

// what a prefix announces: the section's length and its meta's, the meta's
// line end included; undefined when the bytes are not one
export const ParseSectionPrefix = (
  bytes: Uint8Array,
  start: string = START
): { section: number; meta: number } | undefined => {
  const text = UInt8ToString(bytes)
  const found = text.startsWith(start) ? LENGTHS.exec(text.slice(start.length)) : null
  return found ? { section: ToNumber(found[1]!), meta: ToNumber(found[2]!) + 2 } : undefined
}

// the same, for the shared section's prefix
export const ParseSharedPrefix = (bytes: Uint8Array) => ParseSectionPrefix(bytes, SHARED_START)

// A section on its own is no longer part of the merged file, so its marker
// line stops saying it is: the same length of blank comment keeps every
// offset its xref gives, and the page is a plain one-page pdf.
export const BlankMarker = (length: number): string =>
  `${MERGE_HEADER}%${" ".repeat(length - MERGE_HEADER.length - 3)}\r\n`

// the meta right after the prefix, its line end included
export const ParseSectionMeta = (bytes: Uint8Array): PDFPageMeta =>
  JSONDecode(UInt8ToString(Base64Decode(UInt8ToString(bytes.subarray(0, -2)))))

// The integrity line (see the top), for a file `size` bytes long. Its marker
// is the format's version too: a merge from before the sections' meta carried
// `trim` and `bleed` wrote `%PDF-Size `, and such a file is read whole, as the
// plain pdf it also is, its boxes off its own pages.
const INTEGRITY_START = `${MERGE_HEADER}%PDF-Trim `

export const IntegrityLine = (size: number): string =>
  `${INTEGRITY_START}${String(size).padStart(MERGE_SECTION_DIGITS, "0")}\r\n`

export const INTEGRITY_LENGTH = IntegrityLine(0).length

const INTEGRITY = new RegExp(`^(\\d{${MERGE_SECTION_DIGITS}})\r\n$`)

// the file length the line records; undefined when the bytes are not one
export const ParseIntegrity = (bytes: Uint8Array): number | undefined => {
  const text = UInt8ToString(bytes)
  const found = text.startsWith(INTEGRITY_START)
    ? INTEGRITY.exec(text.slice(INTEGRITY_START.length))
    : null
  return found ? ToNumber(found[1]!) : undefined
}
